'use client'

import { forwardRef, InputHTMLAttributes, useCallback } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'

/**
 * Spam protection for the public lead forms (TNA-117). The API applies it in
 * apps/master/lead_protection.py; these are the browser halves.
 */

/** Must match HONEYPOT_FIELD in the API. */
export const HONEYPOT_FIELD = 'website'

/**
 * Hidden input that people never see or tab into. Bots that fill every field
 * fill this one too; the API then answers success but saves and sends nothing.
 * Spread `register(HONEYPOT_FIELD)` onto it.
 */
export const LeadFormHoneypot = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  (props, ref) => (
    <div
      aria-hidden='true'
      style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}
    >
      <label>
        Website
        <input ref={ref} type='text' tabIndex={-1} autoComplete='off' {...props} />
      </label>
    </div>
  ),
)
LeadFormHoneypot.displayName = 'LeadFormHoneypot'

/**
 * Returns `getToken(action)`, resolving to a reCAPTCHA v3 token, or undefined
 * when there is no <ReCaptchaProvider> (no site key in this app's build), the
 * script hasn't loaded yet, or it is blocked. The form submits either way and
 * the API decides whether a token was required.
 */
export const useRecaptchaToken = () => {
  const { executeRecaptcha } = useGoogleReCaptcha()

  return useCallback(
    async (action: string) => {
      try {
        return executeRecaptcha ? await executeRecaptcha(action) : undefined
      } catch {
        return undefined
      }
    },
    [executeRecaptcha],
  )
}

export const TOO_MANY_SUBMISSIONS_MESSAGE =
  'Too many submissions from your network. Please try again in an hour, or reach us on WhatsApp.'
